// @ts-nocheck
/**
 * Pick & Mix — "Zo werken WONDR Liquids": offers the refillable bottle after a
 * liquid is added.
 *
 * Opens when a liquid that was not in the basket yet is added and the basket
 * then holds fewer bottles than different liquids (one bottle per scent).
 * Any way of closing it (cross, backdrop, Escape, "Nee, ik heb al een fles")
 * hides it for the rest of the visit (sessionStorage).
 *
 * The bottle goes in through its own card in the grid via the section's
 * `addItem`, so price, tier and discount work exactly as a click on the card.
 * Markup: snippets/pb-pick-mix-bottle-popup.liquid.
 */
(() => {
  if (window.PBPickMixBottlePopup) return;

  const DISMISSED_KEY = 'pbpm-bottle-popup-dismissed';

  const idSet = (value) => new Set(String(value || '').split(',').filter(Boolean));

  const isDismissed = () => {
    try {
      return window.sessionStorage.getItem(DISMISSED_KEY) === '1';
    } catch {
      return false;
    }
  };

  const rememberDismissed = () => {
    try {
      window.sessionStorage.setItem(DISMISSED_KEY, '1');
    } catch {
      /* no storage: the in-memory flag still covers this page */
    }
  };

  /**
   * @param {object} options
   * @param {HTMLElement} options.root
   * @param {(card: HTMLElement) => void} options.addItem
   * @param {() => Iterable<{variantId: string, quantity: number}>} options.getBasket
   * @param {(card: HTMLElement, quantity: number) => string} options.priceHtml
   * @param {(action: string, props?: object) => void} [options.track]
   */
  function init({ root, addItem, getBasket, priceHtml, track = () => {} } = {}) {
    const popupEl = root?.querySelector('[data-pm-bottle-popup]');
    if (!popupEl) return { shouldOpen: () => false, open() {} };

    const bottleIds = idSet(popupEl.dataset.bottleVariants);
    const liquidIds = idSet(popupEl.dataset.liquidVariants);
    const qtyEl = popupEl.querySelector('[data-pm-bottle-qty]');
    const priceEl = popupEl.querySelector('[data-pm-bottle-price]');
    const minusBtn = popupEl.querySelector('[data-pm-bottle-minus]');

    let dismissed = isDismissed();
    let quantity = 1;
    let restoreFocusTo = null;

    // The bottle's card in the grid; without one (or sold out) there is nothing to add.
    const bottleCard = () => {
      const card = root.querySelector(
        `[data-pm-card][data-product-url="${CSS.escape(popupEl.dataset.bottleUrl || '')}"]`
      );
      return card instanceof HTMLElement && card.dataset.available === 'true' ? card : null;
    };

    const missingBottles = () => {
      let bottles = 0;
      let liquids = 0;

      for (const item of getBasket()) {
        const id = String(item.variantId);
        if (bottleIds.has(id)) bottles += Number(item.quantity) || 0;
        else if (liquidIds.has(id)) liquids += 1;
      }

      return liquids - bottles;
    };

    const paint = () => {
      const card = bottleCard();
      if (qtyEl) qtyEl.textContent = String(quantity);
      if (minusBtn) minusBtn.disabled = quantity <= 1;
      if (priceEl && card) priceEl.innerHTML = priceHtml(card, quantity);
    };

    const setOpen = (open) => {
      popupEl.hidden = !open;
      popupEl.classList.toggle('is-open', open);
      popupEl.setAttribute('aria-hidden', open ? 'false' : 'true');
      document.documentElement.classList.toggle('pbpm-gift-open', open);
      document.body.classList.toggle('pbpm-gift-open', open);

      if (open) {
        window.PBOverlayStack?.push(popupEl);
        return;
      }

      window.PBOverlayStack?.pop(popupEl);
      const target = typeof restoreFocusTo === 'function' ? restoreFocusTo() : restoreFocusTo;
      if (target instanceof HTMLElement && target.isConnected) target.focus({ preventScroll: true });
      restoreFocusTo = null;
    };

    const focusables = () =>
      Array.from(popupEl.querySelectorAll('button:not([disabled]), [href]')).filter(
        (el) => el.offsetWidth > 0 || el.offsetHeight > 0
      );

    const controller = {
      /** Whether adding this variant should open the popup. */
      shouldOpen(variantId) {
        if (dismissed || popupEl.classList.contains('is-open')) return false;
        if (!liquidIds.has(String(variantId)) || !bottleCard()) return false;
        return missingBottles() > 0;
      },

      open({ restoreFocusTo: focusTarget = null } = {}) {
        restoreFocusTo =
          focusTarget || (document.activeElement instanceof HTMLElement ? document.activeElement : null);
        quantity = Math.max(1, missingBottles());
        paint();
        setOpen(true);
        track('opened', { quantity });
        setTimeout(() => popupEl.querySelector('[data-pm-bottle-confirm]')?.focus({ preventScroll: true }), 0);
      },

      confirm() {
        const card = bottleCard();
        // Add before closing: addItem re-renders, and focus goes back afterwards.
        if (card) for (let i = 0; i < quantity; i += 1) addItem(card);
        setOpen(false);
        track('accepted', { quantity });
      },

      dismiss() {
        dismissed = true;
        rememberDismissed();
        setOpen(false);
        track('dismissed');
      }
    };

    popupEl.querySelectorAll('[data-pm-bottle-close]').forEach((el) => {
      el.addEventListener('click', () => controller.dismiss());
    });
    popupEl.querySelector('[data-pm-bottle-confirm]')?.addEventListener('click', () => controller.confirm());
    minusBtn?.addEventListener('click', () => {
      quantity = Math.max(1, quantity - 1);
      paint();
    });
    popupEl.querySelector('[data-pm-bottle-plus]')?.addEventListener('click', () => {
      quantity += 1;
      paint();
    });

    popupEl.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        controller.dismiss();
        return;
      }
      if (event.key !== 'Tab') return;

      // aria-modal hides the page from screen readers; keep the keyboard in too.
      const items = focusables();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
    });

    return controller;
  }

  window.PBPickMixBottlePopup = { init };
})();
